import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
  getDocs,
  Timestamp,
  runTransaction,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import Navbar from "./Navbar";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { getInitials } from "./utils";
import LazyImage from './LazyImage';
import FollowersModal from './FollowersModal';
import SwipeWrapper from './SwipeWrapper';
import Modal from './Modal';
import CollaboratorsModal from './CollaboratorsModal';
import AddToWishlistModal from './AddToWishlistModal';
import { getAffiliateLink, trackClick } from "./utils/affiliateUtils";
import LikesManager from './utils/likesManager';
import { sendFollowNotification, sendLikeNotification } from './notificationService';
import { Capacitor } from '@capacitor/core';

export default function UserPage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [userName, setUserName] = useState(t('common.user'));
  const [userAvatar, setUserAvatar] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [wishlists, setWishlists] = useState([]);
  const [filter, setFilter] = useState("recent");

  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [expandedLists, setExpandedLists] = useState({});
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userWishlists, setUserWishlists] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const location = useLocation();

  // Added state for FollowersModal
  const [showFollowersModal, setShowFollowersModal] = useState({ show: false, type: null });

  // Determina la pagina precedente - same pattern as WishlistPage
  const from = location.state?.from || "/feed";

  // Scroll restoration when returning to UserPage OR scroll to top when first loading
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('fromScroll');
    const fromPage = sessionStorage.getItem('fromPage');
    const fromBottomNav = location.state?.fromBottomNav;
    const shouldRestore = !fromBottomNav && (location.state?.shouldRestoreScroll || (savedScrollPosition && fromPage === `/utente/${userId}`));

    if (shouldRestore && wishlists.length > 0) {
      // Restore scroll position when coming back from wishlist
      const timeoutId = setTimeout(() => {
        const position = location.state?.scrollY || parseInt(savedScrollPosition, 10);
        if (!isNaN(position)) {
          window.scrollTo({
            top: position,
            behavior: 'smooth'
          });

          // Clean up after restoration
          sessionStorage.removeItem('fromScroll');
          sessionStorage.removeItem('fromPage');

          // Clean up location.state if present
          if (location.state?.shouldRestoreScroll) {
            navigate(location.pathname, { 
              state: { 
                ...location.state, 
                shouldRestoreScroll: false,
                scrollY: undefined 
              },
              replace: true
            });
          }
        }
      }, 800);

      return () => clearTimeout(timeoutId);
    } else {
      // Scroll to top when first loading a user page
      window.scrollTo(0, 0);
    }
  }, [userId, wishlists, location.state, navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserName(data.displayName || t('common.user'));
        setUserAvatar(data.avatar || '');
        setIsPremium(data.isPremium || false);
        const followers = data.followers || [];
        const following = data.following || [];
        setFollowerCount(followers.length);
        setFollowingCount(following.length);
        setIsFollowing(followers.includes(auth.currentUser?.uid));
      }
    };
    fetchUserData();
  }, [userId, t]);

  useEffect(() => {
    const q = query(
      collection(db, "wishlists"),
      where("userId", "==", userId),
      where("isPublic", "==", true)
    );

    const unsubscribe = onSnapshot(q, async (snap) => {
      const data = await Promise.all(
        snap.docs.map(async (d) => {
          const wl = d.data();
          const baseWishlist = {
            id: d.id,
            ...wl
          };
          // Use LikesManager to normalize the wishlist data
          return LikesManager.normalizeWishlist(baseWishlist, auth.currentUser?.uid);
        })
      );

      const sorted = filter === "popular"
        ? LikesManager.sortByPopularity(data)
        : LikesManager.sortByDate(data);

      setWishlists(sorted);
    });

    return () => unsubscribe();
  }, [userId, filter]);

  const toggleExpand = (id) => {
    const wishlistElement = document.querySelector(`[data-wishlist-id="${id}"]`);
    const elementTop = wishlistElement ? wishlistElement.offsetTop : 0;

    setExpandedLists(prev => {
      const wasExpanded = prev[id];

      // If we're collapsing, scroll back to the wishlist header
      if (wasExpanded) {
        setTimeout(() => {
          const headerOffset = 80; // Account for fixed navbar
          window.scrollTo({
            top: elementTop - headerOffset,
            behavior: 'smooth'
          });
        }, 100);
      }

      return { ...prev, [id]: !prev[id] };
    });
  };

  const toggleLike = async (wishlistId) => {
    console.log("🔵 [UserPage][toggleLike] INIZIO - wishlistId:", wishlistId, "platform:", Capacitor.getPlatform());
    
    // ✅ FIX: Usa auth.currentUser.uid direttamente invece di currentUserId dallo state
    if (!auth.currentUser) {
      console.log("❌ [UserPage][toggleLike] Utente non autenticato per like");
      return;
    }

    const authenticatedUserId = auth.currentUser.uid;
    console.log("✅ [UserPage][toggleLike] Auth OK, user:", authenticatedUserId);

    // Determine current like state BEFORE transaction
    const currentWishlist = wishlists.find(w => w.id === wishlistId);
    if (!currentWishlist) {
      console.log("❌ [UserPage][toggleLike] Wishlist non trovata:", wishlistId);
      return;
    }

    console.log("✅ [UserPage][toggleLike] Wishlist trovata, userId:", currentWishlist.userId);
    const wasLiked = LikesManager.isLikedByUser(currentWishlist, authenticatedUserId);
    console.log("✅ [UserPage][toggleLike] wasLiked:", wasLiked);

    try {
      console.log("🔵 [UserPage][toggleLike] Chiamo LikesManager.toggleLike...");
      // Use the unified likes manager to handle the toggle
      const updatedWishlist = await LikesManager.toggleLike(
        wishlistId, 
        authenticatedUserId, 
        currentWishlist
      );
      console.log("✅ [UserPage][toggleLike] LikesManager.toggleLike completato");

      // Update local state with the result
      setWishlists(prev => 
        LikesManager.updateWishlistsAfterLike(prev, wishlistId, updatedWishlist)
      );
      console.log("✅ [UserPage][toggleLike] State aggiornato");

      // Invia notifica se stiamo mettendo like (non per unlike)
      if (!wasLiked) {
        console.log("🔵 [UserPage][toggleLike] Era unliked, devo inviare notifica...");
        try {
          console.log("🔵 [UserPage][toggleLike] Fetching wishlist data da Firestore...");
          // Fetch fresh wishlist data from Firestore to get reliable userId
          const wishlistSnap = await getDoc(doc(db, "wishlists", wishlistId));
          console.log("✅ [UserPage][toggleLike] getDoc completato, exists:", wishlistSnap.exists());
          
          if (wishlistSnap.exists()) {
            const wishlistData = wishlistSnap.data();
            const recipientId = wishlistData.userId;
            console.log("✅ [UserPage][toggleLike] recipientId:", recipientId);
            
            if (recipientId && recipientId !== authenticatedUserId) {
              console.log("🔵 [UserPage][toggleLike] Fetching sender name da Firestore...");
              // Ottieni il nome corretto del sender (current user)
              const currentUserDoc = await getDoc(doc(db, "users", authenticatedUserId));
              console.log("✅ [UserPage][toggleLike] getDoc user completato, exists:", currentUserDoc.exists());
              
              const currentUserName = currentUserDoc.exists() ? currentUserDoc.data().displayName || t('common.user') : t('common.user');
              console.log("✅ [UserPage][toggleLike] currentUserName:", currentUserName);
              
              console.log('🔵 [Notifications][UserPage] CHIAMO sendLikeNotification da piattaforma:', Capacitor.getPlatform(), 'sender:', authenticatedUserId, 'recipient:', recipientId, 'wishlist:', wishlistId);
              await sendLikeNotification(authenticatedUserId, currentUserName, recipientId, wishlistId);
              console.log("✅✅✅ [UserPage] Notifica like inviata con successo da piattaforma:", Capacitor.getPlatform());
            } else {
              console.log("ℹ️ [UserPage][toggleLike] Notifica non necessaria - recipientId:", recipientId, "authenticatedUserId:", authenticatedUserId);
            }
          } else {
            console.warn("⚠️ [UserPage][toggleLike] Wishlist non trovata in Firestore:", wishlistId);
          }
        } catch (notifError) {
          console.error("❌❌❌ [UserPage][toggleLike] ERRORE notifica like:", notifError);
          console.error("❌ [UserPage][toggleLike] Error message:", notifError.message);
          console.error("❌ [UserPage][toggleLike] Error code:", notifError.code);
          console.error("❌ [UserPage][toggleLike] Stack trace:", notifError.stack);
        }
      } else {
        console.log("ℹ️ [UserPage][toggleLike] Era già liked, skip notifica (unlike)");
      }
    } catch (error) {
      console.error("❌❌❌ [UserPage][toggleLike] Errore toggle like:", error);
      console.error("❌ [UserPage][toggleLike] Dettagli errore:", {
        code: error.code,
        message: error.message,
        authenticatedUserId,
        wishlistId
      });
    }
    
    console.log("🔵 [UserPage][toggleLike] FINE");
  };

  const toggleFollow = async () => {
    console.log("🔵 [UserPage][toggleFollow] INIZIO - target userId:", userId, "platform:", Capacitor.getPlatform());
    
    // ✅ FIX: Usa auth.currentUser.uid direttamente invece di currentUserId dallo state
    if (!auth.currentUser) {
      console.log("❌ [UserPage][toggleFollow] Utente non autenticato per follow");
      return;
    }

    const authenticatedUserId = auth.currentUser.uid;
    console.log("✅ [UserPage][toggleFollow] Auth OK, user:", authenticatedUserId);

    // Non puoi seguire te stesso
    if (authenticatedUserId === userId) {
      console.log("❌ [UserPage][toggleFollow] Non puoi seguire te stesso");
      return;
    }

    console.log("✅ [UserPage][toggleFollow] Check OK, user:", authenticatedUserId, "target:", userId, "isFollowing:", isFollowing);

    try {
      const userRef = doc(db, "users", userId);
      const currentRef = doc(db, "users", authenticatedUserId);

      console.log("🔵 [UserPage][toggleFollow] Fetching user docs...");
      // Verifica che entrambi i documenti esistano
      const [userDoc, currentDoc] = await Promise.all([
        getDoc(userRef),
        getDoc(currentRef)
      ]);
      console.log("✅ [UserPage][toggleFollow] getDocs completato, userDoc.exists:", userDoc.exists(), "currentDoc.exists:", currentDoc.exists());

      if (!userDoc.exists() || !currentDoc.exists()) {
        console.error("❌ [UserPage][toggleFollow] Uno dei documenti utente non esiste");
        return;
      }

      if (isFollowing) {
        console.log("🔵 [UserPage][toggleFollow] Eseguo UNFOLLOW...");
        await updateDoc(userRef, {
          followers: arrayRemove(authenticatedUserId),
        });
        await updateDoc(currentRef, {
          following: arrayRemove(userId),
        });
        console.log("✅ [UserPage][toggleFollow] Unfollow completato");
      } else {
        console.log("🔵 [UserPage][toggleFollow] Eseguo FOLLOW...");
        await updateDoc(userRef, {
          followers: arrayUnion(authenticatedUserId),
        });
        await updateDoc(currentRef, {
          following: arrayUnion(userId),
        });
        console.log("✅ [UserPage][toggleFollow] Follow completato");
        
        // Invia notifica di follow con il nome corretto del sender (current user)
        try {
          console.log("🔵 [UserPage][toggleFollow] Fetching sender name...");
          const currentUserDoc = await getDoc(currentRef);
          console.log("✅ [UserPage][toggleFollow] getDoc currentUser completato, exists:", currentUserDoc.exists());
          
          const currentUserName = currentUserDoc.exists() ? currentUserDoc.data().displayName || t('common.user') : t('common.user');
          console.log("✅ [UserPage][toggleFollow] currentUserName:", currentUserName);
          
          console.log('🔵 [Notifications][UserPage] CHIAMO sendFollowNotification da piattaforma:', Capacitor.getPlatform(), 'sender:', authenticatedUserId, 'recipient:', userId);
          await sendFollowNotification(authenticatedUserId, currentUserName, userId);
          console.log("✅✅✅ [UserPage][toggleFollow] Notifica follow inviata con successo da piattaforma:", Capacitor.getPlatform());
        } catch (notifError) {
          console.error("❌❌❌ [UserPage][toggleFollow] ERRORE notifica follow:", notifError);
          console.error("❌ [UserPage][toggleFollow] Error message:", notifError.message);
          console.error("❌ [UserPage][toggleFollow] Error code:", notifError.code);
          console.error("❌ [UserPage][toggleFollow] Stack trace:", notifError.stack);
        }
      }

      console.log("🔵 [UserPage][toggleFollow] Aggiorno state UI...");
      setIsFollowing(!isFollowing);
      setFollowerCount((prev) => prev + (isFollowing ? -1 : 1));
      console.log("✅ [UserPage][toggleFollow] State UI aggiornato");
    } catch (error) {
      console.error("❌❌❌ [UserPage][toggleFollow] Errore toggle follow:", error);
      console.error("❌ [UserPage][toggleFollow] Dettagli errore:", {
        code: error.code,
        message: error.message,
        authenticatedUserId,
        userId,
        isFollowing
      });
    }
    
    console.log("🔵 [UserPage][toggleFollow] FINE");
  };

  const openModal = async (product, event) => {
    setSelectedProduct(product);

    // Query per le wishlist di cui l'utente è proprietario
    const ownWishlistsQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", currentUserId),
      where("archived", "==", false)
    );

    // Query per le wishlist dove l'utente è collaboratore
    const collaboratorWishlistsQuery = query(
      collection(db, "wishlists"),
      where("collaborators", "array-contains", currentUserId),
      where("archived", "==", false)
    );

    const [ownSnap, collaboratorSnap] = await Promise.all([
      getDocs(ownWishlistsQuery),
      getDocs(collaboratorWishlistsQuery)
    ]);

    const ownLists = ownSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: true 
    }));

    const collaboratorLists = collaboratorSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: false 
    }));

    const allLists = [...ownLists, ...collaboratorLists];
    setUserWishlists(allLists);
    setShowModal(true);
  };

  const addProductToWishlist = async (wishlistId) => {
    if (!selectedProduct) return;
    const ref = doc(db, "wishlists", wishlistId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const updated = Array.isArray(data.products)
      ? [selectedProduct, ...data.products]
      : [selectedProduct];
    await updateDoc(ref, { products: updated });
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    if (!currentUserId) return;
    const fetchWishlists = async () => {
      const q = query(
        collection(db, "wishlists"),
        where("userId", "==", currentUserId),
        where("archived", "==", false)
      );
      const snap = await getDocs(q);
      const lists = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserWishlists(lists);
    };
    fetchWishlists();
  }, [currentUserId]);

  if (wishlists.length === 0) {
    return (
      <SwipeWrapper redirectTo={from}>
        <div style={{ fontFamily: "Poppins, sans-serif", background: "#fff", minHeight: "100vh", paddingBottom: "80px" }}>
          <Navbar />
          <div style={{ padding: 20, paddingTop: "65px" }}>
            {/* Profile Header - Same as main layout */}
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center", 
              marginBottom: "24px",
              paddingBottom: "20px",
              borderBottom: "1px solid #e0e0e0"
            }}>
              {/* Avatar */}
              {userAvatar ? (
                <LazyImage 
                  src={userAvatar} 
                  alt="avatar" 
                  style={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: "50%", 
                    objectFit: "cover",
                    border: "3px solid #000",
                    marginBottom: "12px"
                  }} 
                />
              ) : (
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "#000",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                  fontWeight: "bold",
                  border: "3px solid #000",
                  marginBottom: "12px"
                }}>
                  {getInitials(userName)}
                </div>
              )}
              
              {/* Username with Premium Badge */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <h2 style={{ 
                  fontSize: 24, 
                  fontWeight: "bold", 
                  margin: 0,
                  textAlign: "center"
                }}>{userName}</h2>
                {isPremium && (
                  <div style={{
                    background: "linear-gradient(135deg, #FFD700, #FFA500)",
                    color: "#000",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px"
                  }}>
                    <span>👑</span>
                    <span>PREMIUM</span>
                  </div>
                )}
              </div>

              {/* Stats Row */}
              <div style={{ 
                display: "flex", 
                gap: "24px", 
                marginBottom: "16px",
                fontSize: 15
              }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: "bold", fontSize: 18 }}>0</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{t('userPage.publicWishlists')}</div>
                </div>
                <div 
                  style={{ textAlign: "center", cursor: "pointer" }}
                  onClick={() => setShowFollowersModal({ show: true, type: 'followers' })}
                >
                  <div style={{ fontWeight: "bold", fontSize: 18 }}>{followerCount}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{t('profile.followers')}</div>
                </div>
                <div 
                  style={{ textAlign: "center", cursor: "pointer" }}
                  onClick={() => setShowFollowersModal({ show: true, type: 'following' })}
                >
                  <div style={{ fontWeight: "bold", fontSize: 18 }}>{followingCount}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{t('profile.following')}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: "bold", fontSize: 18 }}>0</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{t('feed.likes')}</div>
                </div>
              </div>

              {/* Follow Button */}
              {auth.currentUser?.uid !== userId && (
                <button
                  onClick={toggleFollow}
                  style={{ 
                    background: isFollowing ? "#f0f0f0" : "#000", 
                    color: isFollowing ? "#000" : "#fff", 
                    border: "1px solid #000",
                    padding: "10px 32px", 
                    borderRadius: "8px", 
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}
                >
                  {isFollowing ? t('feed.unfollow') : t('feed.follow')}
                </button>
              )}
            </div>

            {/* No wishlists message */}
            <div style={{ 
              textAlign: "center", 
              padding: "40px 20px",
              color: "#666",
              fontSize: "16px"
            }}>
              {t('userPage.noPublicWishlists')}
            </div>
          </div>
        </div>
      </SwipeWrapper>
    );
  }



  return (
    <SwipeWrapper 
      redirectTo={from}
    >
      <div style={{ fontFamily: "Poppins, sans-serif", background: "#fff", color: "#000", minHeight: "100vh", paddingBottom: "80px" }}>
        <Navbar />

        <div style={{ padding: 20, paddingTop: "65px" }}>
          {/* Profile Header - Centered Avatar */}
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            marginBottom: "24px",
            paddingBottom: "20px",
            borderBottom: "1px solid #e0e0e0"
          }}>
            {/* Avatar */}
            {userAvatar ? (
              <LazyImage 
                src={userAvatar} 
                alt="avatar" 
                style={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: "50%", 
                  objectFit: "cover",
                  border: "3px solid #000",
                  marginBottom: "12px",
                  display: "block",
                  margin: "0 auto 12px auto"
                }} 
              />
            ) : (
              <div style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#000",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                fontWeight: "bold",
                border: "3px solid #000",
                marginBottom: "12px"
              }}>
                {getInitials(userName)}
              </div>
            )}
            
            {/* Username with Premium Badge */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <h2 style={{ 
                fontSize: 24, 
                fontWeight: "bold", 
                margin: 0,
                textAlign: "center"
              }}>{userName}</h2>
              {isPremium && (
                <div style={{
                  background: "linear-gradient(135deg, #FFD700, #FFA500)",
                  color: "#000",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>👑</span>
                  <span>PREMIUM</span>
                </div>
              )}
            </div>

            {/* Stats Row */}
            <div style={{ 
              display: "flex", 
              gap: "24px", 
              marginBottom: "16px",
              fontSize: 15
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: "bold", fontSize: 18 }}>{wishlists.length}</div>
                <div style={{ color: "#666", fontSize: 13 }}>{t('userPage.publicWishlists')}</div>
              </div>
              <div 
                style={{ textAlign: "center", cursor: "pointer" }}
                onClick={() => setShowFollowersModal({ show: true, type: 'followers' })}
              >
                <div style={{ fontWeight: "bold", fontSize: 18 }}>{followerCount}</div>
                <div style={{ color: "#666", fontSize: 13 }}>{t('profile.followers')}</div>
              </div>
              <div 
                style={{ textAlign: "center", cursor: "pointer" }}
                onClick={() => setShowFollowersModal({ show: true, type: 'following' })}
              >
                <div style={{ fontWeight: "bold", fontSize: 18 }}>{followingCount}</div>
                <div style={{ color: "#666", fontSize: 13 }}>{t('profile.following')}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: "bold", fontSize: 18 }}>{LikesManager.getTotalLikesForUser(wishlists)}</div>
                <div style={{ color: "#666", fontSize: 13 }}>{t('feed.likes')}</div>
              </div>
            </div>

            {/* Follow Button */}
            {auth.currentUser?.uid !== userId && (
              <button
                onClick={toggleFollow}
                style={{ 
                  background: isFollowing ? "#f0f0f0" : "#000", 
                  color: isFollowing ? "#000" : "#fff", 
                  border: "1px solid #000",
                  padding: "10px 32px", 
                  borderRadius: "8px", 
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s ease"
                }}
              >
                {isFollowing ? t('feed.unfollow') : t('feed.follow')}
              </button>
            )}
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              marginBottom: "20px",
              padding: "10px 14px",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              color: "#000",
              fontSize: "14px",
              fontWeight: "500",
              background: "#fff",
              cursor: "pointer"
            }}
          >
            <option value="recent">{t('feed.sortRecent')}</option>
            <option value="popular">{t('feed.sortPopular')}</option>
          </select>

          {wishlists.map((w) => {
            const expanded = expandedLists[w.id];
            const visibleProducts = expanded ? w.products : (w.products || []).slice(0, 3);
            return (
              <div 
                  key={w.id}
                  data-wishlist-id={w.id}
                  style={{
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "16px",
                    padding: "18px",
                    marginBottom: "16px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "box-shadow 0.2s ease"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.12)"}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)"}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <h4
                      onClick={() => {
                        sessionStorage.setItem('fromScroll', window.scrollY.toString());
                        sessionStorage.setItem('fromPage', `/utente/${userId}`);
                        navigate(`/wishlist/${w.id}`, { state: { from: `/utente/${userId}` } });
                      }}
                      style={{ fontSize: "16px", fontWeight: "bold", cursor: "pointer", textDecoration: "underline", margin: 0 }}
                    >
                      {w.name}
                    </h4>
                    {(w.isShared || (w.collaborators && w.collaborators.length > 0)) && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCollaboratorsModal(w.id);
                        }}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                          border: "2px solid #000",
                          borderRadius: "50%",
                          backgroundColor: "transparent"
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <span
                    onClick={() => toggleLike(w.id)}
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {LikesManager.isLikedByUser(w, currentUserId) ? <FaHeart color="#000" /> : <FaRegHeart color="#000" />}
                    {LikesManager.getLikesCount(w)}
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {visibleProducts.map((p, i) => (
                    <div key={i} style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "12px", 
                      background: "#fafafa", 
                      border: "1px solid #e8e8e8", 
                      borderRadius: "12px", 
                      padding: "12px",
                      transition: "background 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#fafafa"}
                    >
                      <LazyImage 
                        src={p.image} 
                        alt={p.title} 
                        style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "10px" }} 
                      />
                      <a
                              href={getAffiliateLink(p)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                flex: 1, 
                                color: "#000", 
                                textDecoration: "none",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textAlign: "center",
                                wordBreak: "break-word",
                                minWidth: 0
                              }}
                              onClick={() => trackClick(p.id, w.id, 'amazon')}
                            >{p.title}</a>
                      <button onClick={(event) => openModal(p, event)} style={{ 
                        background: "#000", 
                        color: "#fff", 
                        border: "none", 
                        padding: "8px 12px", 
                        borderRadius: "8px", 
                        fontSize: "18px",
                        cursor: "pointer",
                        transition: "transform 0.2s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                      >＋</button>
                    </div>
                  ))}
                </div>

                {w.products?.length > 3 && (
                  <button
                    onClick={() => toggleExpand(w.id)}
                    style={{ marginTop: "8px", background: "none", border: "none", color: "#000", cursor: "pointer", fontSize: "14px" }}
                  >
                    {expanded ? t('wishlist.showLess') : `+ ${w.products.length - 3} ${t('wishlist.moreProducts')}`}
                  </button>
                )}
              </div>
            );
          })}

          <AddToWishlistModal
            showModal={showModal}
            onClose={closeModal}
            userWishlists={userWishlists}
            onAddToWishlist={addProductToWishlist}
          />

          {showCollaboratorsModal && (
            <CollaboratorsModal
              wishlistId={showCollaboratorsModal}
              wishlist={wishlists.find(w => w.id === showCollaboratorsModal)}
              currentUserId={currentUserId}
              onClose={() => setShowCollaboratorsModal(null)}
              viewOnly={wishlists.find(w => w.id === showCollaboratorsModal)?.userId !== currentUserId}
            />
          )}

          {showFollowersModal.show && (
            <FollowersModal
              isOpen={showFollowersModal.show}
              onClose={() => setShowFollowersModal({ show: false, type: null })}
              userId={userId}
              type={showFollowersModal.type}
              userDisplayName={userName}
            />
          )}
        </div>
      </div>
    </SwipeWrapper>
  );
}